<template>
    <div
        class="custouch-form-component text-left"
        :style="{
            '--minisite-form-theme-color': data.style.themeColor,
            '--primary-color': data.style.themeColor
        }"
        @click.capture="activateThemeColor"
        @focusin.capture="activateThemeColor">
        <div v-if="loading" class="form-loading">
            <van-loading />
        </div>
        <div v-else-if="loadFailed" class="form-error">表单加载失败，请稍后重试</div>
        <div v-show="!loading && !loadFailed" ref="formWrapper"></div>
    </div>
</template>

<script>
    import Vue from "vue"
    import { Loading } from "vant"
    import { getFormDetail } from "@/api/form"
    import { loadFormKit } from "@/utils/formKit"
    import { getChannel, getUtm } from "@/utils/preflight"
    import { mapGetters } from "vuex";

    Vue.use(Loading)

    export default {
        name: "FormComponent",
        inject: {
            minisiteAccess: { default: () => ({ skipAccess: false }) }
        },
        props: {
            data: {
                type: Object,
                default() {
                    return {}
                }
            }
        },
        data() {
            return {
                loading: false,
                loadFailed: false,
                formInstance: null,
                agreementDialogObserver: null
            }
        },
        computed: {
            ...mapGetters("common", ["WechatInfo"]),
            form() {
                return this.data.contents?.[0]?.form || null
            }
        },
        mounted() {
            this.mountForm()
        },
        beforeDestroy() {
            this.clearAgreementDialogDecoration()

            if (this.formInstance) {
                this.formInstance.$destroy()
                this.formInstance = null
            }
        },
        methods: {
            activateThemeColor() {
                // 协议弹窗挂载在 body 下，需要记录当前操作表单的主题色供弹窗使用。
                document.documentElement.style.setProperty(
                    "--minisite-form-active-theme-color",
                    this.data.style.themeColor
                )
                window.requestAnimationFrame(this.decorateAgreementDialog)
            },
            decorateAgreementDialog() {
                const dialog = document.querySelector(".my-agreement-dialog")
                const overlays = Array.from(document.querySelectorAll(".van-overlay"))
                const overlay = overlays.reverse().find((item) => window.getComputedStyle(item).display !== "none")

                if (!dialog || !overlay || window.getComputedStyle(dialog).display === "none") {
                    return
                }

                this.clearAgreementDialogDecoration()
                overlay.classList.add("minisite-form-agreement-overlay")

                // 协议弹窗关闭后移除专用遮罩样式，避免影响微站中的其他 Vant 弹窗。
                this.agreementDialogObserver = new MutationObserver(() => {
                    if (window.getComputedStyle(dialog).display === "none") {
                        this.clearAgreementDialogDecoration()
                    }
                })
                this.agreementDialogObserver.observe(dialog, {
                    attributes: true,
                    attributeFilter: ["class", "style"]
                })
            },
            clearAgreementDialogDecoration() {
                this.agreementDialogObserver?.disconnect()
                this.agreementDialogObserver = null
                document
                    .querySelector(".minisite-form-agreement-overlay")
                    ?.classList.remove("minisite-form-agreement-overlay")
            },
            async mountForm() {
                if (!this.form) {
                    return
                }

                this.loading = true
                this.loadFailed = false

                try {
                    const formKit = await loadFormKit()
                    const formDetail = await getFormDetail(this.form.id)
                    formDetail.structure.styles.content.theme.themeColor = this.data.style.themeColor || "#fd6f1c"
                    formDetail.structure.styles.content.theme.button.backgroundColor = this.data.style.themeColor
                    formDetail.structure.styles.content.theme.title.color = this.data.style.titleColor || "#253858"
                    formDetail.structure.styles.content.theme.description.color =
                        this.data.style.descriptionColor || "#6D788C"
                    formDetail.structure.styles.content.theme.option.color = "#253858"
                    const mountPoint = document.createElement("div")
                    this.$refs.formWrapper.appendChild(mountPoint)

                    this.formInstance = new Vue({
                        render: (h) =>
                            h(formKit, {
                                props: {
                                    metadata: {
                                        ...formDetail.metadata,
                                        presets: {},
                                        outerConfigs: {
                                            module: "site",
                                            fields: {
                                                ...getUtm(),
                                                m_ref: getChannel() ?? undefined,
                                                WechatOpenId: this.WechatInfo.openid || null,
                                                WechatUnionId: this.WechatInfo.unionid || null
                                            },
                                            httpHeaders: {
                                                "X-Tracking-ID": null
                                            }
                                        }
                                    },
                                    structure: formDetail.structure,
                                    skipAccess: this.minisiteAccess.skipAccess,
                                    shieldFormDocumentTitle: true
                                }
                            })
                    })
                    this.formInstance.$mount(mountPoint)
                } catch (error) {
                    this.loadFailed = true
                    console.error("Failed to load minisite form", error)
                } finally {
                    this.loading = false
                }
            }
        }
    }
</script>

<style lang="less" src="@/styles/form.less"></style>

<style lang="less" scoped>
    .custouch-form-component {
        width: 100%;
    }

    .form-loading,
    .form-error {
        min-height: 120px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .form-error {
        color: #969799;
        font-size: 14px;
    }
</style>
